import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartItem {
  productId: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
  variant?: string;
  imageUrl?: string;
  boutiqueName?: string;
  boutiqueId?: string;
}

interface CartState {
  items: CartItem[];
}

function loadFromStorage(): CartItem[] {
  try {
    const raw = localStorage.getItem('nomadme_cart');
    if (raw) return JSON.parse(raw) as CartItem[];
  } catch {}
  return [];
}

function saveToStorage(items: CartItem[]) {
  try {
    localStorage.setItem('nomadme_cart', JSON.stringify(items));
  } catch {}
}

const cartSlice = createSlice({
  name: 'cart',
  initialState: { items: loadFromStorage() } as CartState,
  reducers: {
    addToCart(state, action: PayloadAction<CartItem>) {
      const incoming = action.payload;
      const existing = state.items.find(
        (i) => i.productId === incoming.productId && (i.variant ?? '') === (incoming.variant ?? '')
      );
      if (existing) {
        existing.quantity += incoming.quantity;
      } else {
        state.items.push(incoming);
      }
      saveToStorage(state.items);
    },
    removeFromCart(state, action: PayloadAction<{ productId: string; variant?: string }>) {
      state.items = state.items.filter(
        (i) => !(i.productId === action.payload.productId && (i.variant ?? '') === (action.payload.variant ?? ''))
      );
      saveToStorage(state.items);
    },
    updateQuantity(state, action: PayloadAction<{ productId: string; variant?: string; quantity: number }>) {
      const item = state.items.find(
        (i) => i.productId === action.payload.productId && (i.variant ?? '') === (action.payload.variant ?? '')
      );
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
        saveToStorage(state.items);
      }
    },
    clearCart(state) {
      state.items = [];
      saveToStorage([]);
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
